import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  WebsiteConfig, readWebsiteConfig, isVideoMode, isValidWebUrl,
  iframeSandbox, iframeAllow, buildRenderSrc, fitToObjectFit, isHlsUrl, isYouTubeUrl,
  autoScrollPps,
} from "../../Pages/Template/Edit/websiteConfig";

/**
 * Shared render surface for a "website"-family zone. Used identically by
 * the Template Builder canvas, the Preview dialog and the Web Player so
 * "what you preview is what the device shows".
 *
 * `interactive` controls whether the embedded content receives pointer
 * events. In the editor this is normally false (so the operator can
 * select/drag/resize the zone) and only turned on for the dedicated
 * "Interact with embedded content" mode. On the device it is driven by
 * the zone's `allow_interaction` / map interaction settings.
 *
 * A failure in one zone is contained: every branch renders a message
 * block instead of throwing, so one broken URL never blanks the whole
 * template.
 */
export interface WebZoneViewProps {
  config: Record<string, any> | null | undefined;
  /** true → embedded content is clickable/scrollable. */
  interactive: boolean;
  /** Bumping this (e.g. on a device refresh command) forces a reload. */
  reloadNonce?: number;
}

type LoadState = "loading" | "loaded" | "error" | "blocked";

const MSG = {
  invalidUrl: "Enter a valid website URL.",
  invalidEmbed: "Enter a valid iframe URL or supported embed code.",
  blocked: "This website does not allow embedding. Use an embeddable URL or a direct video URL.",
  blockedGeneric: "This content provider does not allow embedding.",
  unreachable: "The website could not be loaded. Check the URL and internet connection.",
  videoError: "The video could not be played on this device.",
  offline: "The website could not be loaded. Check the URL and internet connection.",
};

const WebZoneView: React.FC<WebZoneViewProps> = ({ config, interactive, reloadNonce = 0 }) => {
  const cfg: WebsiteConfig = readWebsiteConfig(config);

  if (!cfg.url || !isValidWebUrl(cfg.url)) {
    return <Message text={cfg.url ? MSG.invalidUrl : "No URL set"} bg={cfg.background} tone="muted" />;
  }

  // A native <video> element can only play real media files. YouTube (and
  // other page URLs) must go through the iframe player even if the stored
  // mode is direct_video — never feed a YouTube URL to <video>.
  if (cfg.display_mode === "direct_video" && !isYouTubeUrl(cfg.url)) {
    return <DirectVideo cfg={cfg} reloadNonce={reloadNonce} />;
  }
  return <IframeZone cfg={cfg} interactive={interactive} reloadNonce={reloadNonce} />;
};

/* ── Direct browser-native video ──────────────────────────────────── */

const DirectVideo: React.FC<{ cfg: WebsiteConfig; reloadNonce: number }> = ({ cfg, reloadNonce }) => {
  const [state, setState] = useState<LoadState>("loading");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const objectFit = fitToObjectFit(cfg.fit);
  // Autoplay reliability: force mute when autoplay is on (browsers block
  // autoplay-with-sound). Operator was already warned in the inspector.
  const muted = cfg.autoplay ? true : cfg.muted;

  useEffect(() => { setState("loading"); }, [cfg.url, reloadNonce]);

  const hls = isHlsUrl(cfg.url);
  const nativeHls = useMemo(() => {
    const v = document.createElement("video");
    return v.canPlayType("application/vnd.apple.mpegurl") !== "";
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: cfg.background || "#000", position: "relative" }}>
      {hls && !nativeHls ? (
        <Message text={MSG.videoError} bg={cfg.background} tone="error" />
      ) : (
        <video
          key={`${cfg.url}#${reloadNonce}`}
          ref={videoRef}
          src={cfg.url}
          autoPlay={cfg.autoplay}
          muted={muted}
          loop={cfg.loop}
          controls={cfg.controls}
          playsInline
          onCanPlay={() => setState("loaded")}
          onError={() => setState("error")}
          style={{ width: "100%", height: "100%", objectFit, display: "block", background: cfg.background || "#000" }}
          {...({ "webkit-playsinline": "true" } as Record<string, string>)}
        />
      )}
      {state === "loading" && !hls && <Overlay text="Loading…" bg={cfg.background} />}
      {state === "error" && <Message text={MSG.videoError} bg={cfg.background} tone="error" />}
    </div>
  );
};

/* ── Iframe (website / video / map / generic) ─────────────────────── */

const IframeZone: React.FC<{ cfg: WebsiteConfig; interactive: boolean; reloadNonce: number }> = ({
  cfg, interactive, reloadNonce,
}) => {
  const [state, setState] = useState<LoadState>("loading");
  const [offline, setOffline] = useState<boolean>(typeof navigator !== "undefined" && !navigator.onLine);
  const src = buildRenderSrc(cfg);
  const sandbox = iframeSandbox(cfg);
  const allow = iframeAllow(cfg);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [box, setBox] = useState<Box>({ w: 0, h: 0 });

  // Auto-refresh at the configured interval by remounting the iframe.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    if (!cfg.refresh_interval || cfg.refresh_interval <= 0) return;
    const id = window.setInterval(() => setRefreshTick((n) => n + 1), cfg.refresh_interval * 60_000);
    return () => window.clearInterval(id);
  }, [cfg.refresh_interval]);

  // Online / offline awareness.
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // "Unable to load" fallback: if the frame never signals load within
  // the grace window we surface the unreachable message. X-Frame-Options
  // / CSP blocking cannot be read cross-origin from JS, so this is a
  // best-effort signal — we never try to bypass the provider's policy.
  const loadedRef = useRef(false);
  useEffect(() => {
    loadedRef.current = false;
    setState("loading");
    const id = window.setTimeout(() => {
      if (!loadedRef.current) setState("blocked");
    }, 8000);
    return () => window.clearTimeout(id);
  }, [src, reloadNonce, refreshTick]);

  const showScroll = cfg.show_scrollbars;
  const pointer = interactive ? "auto" : "none";

  // The advanced path (measured viewport + scale + translate) is only used
  // when a zone actually needs it: Native/Scroll fit, an explicit virtual
  // viewport, page zoom, or auto-scroll. Plain fit/fill/stretch zones keep
  // the simple full-bleed iframe so existing templates render identically.
  const pps = autoScrollPps(cfg);
  const usesViewport =
    cfg.fit === "native" ||
    cfg.viewport_width != null ||
    cfg.viewport_height != null ||
    (cfg.page_zoom != null && cfg.page_zoom !== 100) ||
    pps > 0;

  // Measure the clipping container so pixel geometry stays correct across
  // 1920×1080 landscape, portrait and any zone size (all percentage-based).
  useLayoutEffect(() => {
    if (!usesViewport) return;
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox((b) => (b.w === r.width && b.h === r.height ? b : { w: r.width, h: r.height }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [usesViewport]);

  const geo = useMemo(
    () => (usesViewport && box.w > 0 && box.h > 0 ? computeGeometry(box, cfg) : null),
    [usesViewport, box, cfg],
  );

  // Auto-scroll: translate the iframe element within its clipping box. This
  // never reaches into the framed document, so it works for cross-origin
  // sites and never violates CSP / X-Frame-Options / same-origin policy.
  useEffect(() => {
    const el = iframeRef.current;
    if (!el || !geo) return;

    const vertical = cfg.auto_scroll_direction === "down" || cfg.auto_scroll_direction === "up";
    const forward = cfg.auto_scroll_direction === "down" || cfg.auto_scroll_direction === "right";
    const extent = vertical ? geo.extentY : geo.extentX;

    const apply = (posPx: number) => {
      const ox = vertical ? 0 : posPx;
      const oy = vertical ? posPx : 0;
      el.style.transform =
        `translate(${geo.baseTx - ox}px, ${geo.baseTy - oy}px) scale(${geo.scaleX}, ${geo.scaleY})`;
    };

    const initial = Math.min(Math.max(0, cfg.initial_scroll_position || 0), extent);
    let pos = forward ? initial : Math.max(0, extent - initial);
    let dir = forward ? 1 : -1;
    apply(pos);

    if (pps <= 0 || extent <= 0) return; // static — nothing to animate

    let raf = 0;
    let last = 0;
    let phase: "delay" | "run" | "pause" = "delay";
    let phaseUntil = 0;
    const startDelay = cfg.auto_scroll_start_delay_ms || 0;
    const endPause = cfg.auto_scroll_end_pause_ms || 0;
    const end = cfg.auto_scroll_end_behavior;

    const step = (ts: number) => {
      if (!last) { last = ts; phaseUntil = ts + startDelay; }
      const dt = Math.min(0.1, (ts - last) / 1000); // clamp long frame gaps
      last = ts;

      if (phase !== "run") {
        if (ts >= phaseUntil) phase = "run";
      } else {
        pos += dir * pps * dt;
        if (pos >= extent) {
          pos = extent;
          if (end === "stop") { apply(pos); return; }
          if (end === "reverse") { dir = -1; phase = "pause"; phaseUntil = ts + endPause; }
          else { pos = 0; phase = "pause"; phaseUntil = ts + endPause; } // restart
        } else if (pos <= 0) {
          pos = 0;
          if (dir < 0) {
            if (end === "stop") { apply(pos); return; }
            if (end === "reverse") { dir = 1; phase = "pause"; phaseUntil = ts + endPause; }
            else { pos = extent; phase = "pause"; phaseUntil = ts + endPause; } // restart (from up/left)
          }
        }
        apply(pos);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [
    geo, pps, cfg.auto_scroll_direction, cfg.auto_scroll_end_behavior,
    cfg.auto_scroll_start_delay_ms, cfg.auto_scroll_end_pause_ms,
    cfg.initial_scroll_position, reloadNonce, refreshTick,
  ]);

  const iframeCommon = {
    src,
    title: cfg.title ?? "Embedded content",
    sandbox,
    allow,
    allowFullScreen: cfg.allow_fullscreen,
    // Providers like YouTube reject embedded playback with no Referer
    // (Error 153). Send the app origin — never `no-referrer` here.
    referrerPolicy: "strict-origin-when-cross-origin" as React.HTMLAttributeReferrerPolicy,
    scrolling: showScroll ? "yes" : "no",
    onLoad: () => { loadedRef.current = true; setState("loaded"); },
    onError: () => setState("error"),
  };

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", background: cfg.background || "#000", position: "relative", overflow: "hidden" }}
    >
      {offline ? (
        <Message text={MSG.offline} bg={cfg.background} tone="error" />
      ) : usesViewport ? (
        <iframe
          key={`${src}#${reloadNonce}#${refreshTick}`}
          ref={iframeRef}
          {...iframeCommon}
          style={{
            position: "absolute", top: 0, left: 0,
            width: geo ? `${geo.vw}px` : "100%",
            height: geo ? `${geo.vh}px` : "100%",
            border: 0, display: "block",
            transformOrigin: "top left",
            transform: geo
              ? `translate(${geo.baseTx}px, ${geo.baseTy}px) scale(${geo.scaleX}, ${geo.scaleY})`
              : undefined,
            background: cfg.background || "#000",
            pointerEvents: pointer as React.CSSProperties["pointerEvents"],
            visibility: geo ? "visible" : "hidden",
          }}
        />
      ) : (
        <iframe
          key={`${src}#${reloadNonce}#${refreshTick}`}
          {...iframeCommon}
          style={{
            width: "100%", height: "100%", border: 0, display: "block",
            background: cfg.background || "#000",
            pointerEvents: pointer as React.CSSProperties["pointerEvents"],
            overflow: showScroll ? "auto" : "hidden",
          }}
        />
      )}
      {!offline && state === "loading" && <Overlay text="Loading…" bg={cfg.background} />}
      {!offline && state === "blocked" && (
        <Message
          text={cfg.display_mode === "website" ? MSG.blocked : MSG.blockedGeneric}
          bg={cfg.background}
          tone="warn"
          note={MSG.unreachable}
        />
      )}
      {!offline && state === "error" && <Message text={MSG.unreachable} bg={cfg.background} tone="error" />}
    </div>
  );
};

/* ── Viewport geometry (measured-box → scaled iframe) ─────────────────── */

type Box = { w: number; h: number };

interface Geometry {
  vw: number; vh: number;
  scaleX: number; scaleY: number;
  extentX: number; extentY: number;
  baseTx: number; baseTy: number;
}

/**
 * Compute the virtual viewport size, per-axis scale and the base translate
 * for a website zone. `native` fits to width (so vertical content overflows
 * and can be scrolled); fit/fill/stretch behave like object-fit contain/
 * cover/fill. `page_zoom` multiplies the resulting scale.
 */
function computeGeometry(boxPx: Box, cfg: WebsiteConfig): Geometry {
  const zoom = Math.max(0.1, (cfg.page_zoom || 100) / 100);
  const vw = cfg.viewport_width && cfg.viewport_width > 0 ? cfg.viewport_width : boxPx.w;
  const vh = cfg.viewport_height && cfg.viewport_height > 0 ? cfg.viewport_height : boxPx.h;

  let scaleX: number;
  let scaleY: number;
  let topLeft: boolean;

  if (cfg.fit === "stretch") {
    scaleX = boxPx.w / vw; scaleY = boxPx.h / vh; topLeft = true;
  } else if (cfg.fit === "fill") {
    const s = Math.max(boxPx.w / vw, boxPx.h / vh); scaleX = s; scaleY = s; topLeft = false;
  } else if (cfg.fit === "native") {
    const s = (boxPx.w / vw) * zoom; scaleX = s; scaleY = s; topLeft = true;
  } else {
    const s = Math.min(boxPx.w / vw, boxPx.h / vh); scaleX = s; scaleY = s; topLeft = false;
  }
  if (cfg.fit !== "native") { scaleX *= zoom; scaleY *= zoom; }

  const sw = vw * scaleX;
  const sh = vh * scaleY;
  return {
    vw, vh, scaleX, scaleY,
    extentX: Math.max(0, sw - boxPx.w),
    extentY: Math.max(0, sh - boxPx.h),
    baseTx: topLeft ? 0 : -(sw - boxPx.w) / 2,
    baseTy: topLeft ? 0 : -(sh - boxPx.h) / 2,
  };
}

/* ── State UI ─────────────────────────────────────────────────────── */

const Overlay: React.FC<{ text: string; bg?: string }> = ({ text }) => (
  <div style={{
    position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(0,0,0,0.35)", color: "#fff", fontSize: 13, fontFamily: "Inter, sans-serif",
    pointerEvents: "none",
  }}>
    {text}
  </div>
);

const Message: React.FC<{ text: string; bg?: string; tone: "muted" | "warn" | "error"; note?: string }> = ({
  text, bg, tone, note,
}) => {
  const color = tone === "error" ? "#fca5a5" : tone === "warn" ? "#fcd34d" : "rgba(255,255,255,0.6)";
  return (
    <div style={{
      width: "100%", height: "100%", background: bg || "rgba(255,255,255,0.04)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: 12, color, fontFamily: "Inter, sans-serif",
      border: "1px dashed rgba(255,255,255,0.18)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, maxWidth: "90%" }}>{text}</div>
      {note && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 6, maxWidth: "90%" }}>{note}</div>}
    </div>
  );
};

export default WebZoneView;
